/**
 * mb-product.js — The Mango Butter Brand
 * Product page logic: gallery, variants, cart, tabs, sticky bar,
 * recently viewed, related products, animations.
 *
 * @version 2.0.0
 */

'use strict';

/* ============================================================
   TYPE DEFINITIONS (JSDoc)
   ============================================================ */

/** @typedef {{ id: number, title: string, price: number, compare_at_price: number|null, available: boolean, options: string[], featured_image: {src: string, alt: string}|null }} ShopifyVariant */
/** @typedef {{ items: Array<{id: number, quantity: number, title: string, price: number, line_price: number, key: string}>, total_price: number, item_count: number }} ShopifyCart */
/** @typedef {{ id: number, title: string, url: string, image: string, formattedPrice: string, price: number }} ViewedProduct */
/** @typedef {{ id: number, title: string, handle: string, price: number, compare_at_price: number|null, featured_image: string|null, url: string, variants?: Array<{id:number}> }} RecommendedProduct */


/* ============================================================
   UTILITIES
   ============================================================ */

/**
 * Format Shopify price (integer cents) → currency string.
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, '${{amount}}');
  }
  return '$' + (cents / 100).toFixed(2);
}

/**
 * Debounce a function.
 * @param {Function} fn
 * @param {number} delay
 * @returns {Function}
 */
function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Dispatch a custom event.
 * @param {string} name
 * @param {any} detail
 * @param {Element|Window} [target]
 */
function dispatch(name, detail, target = window) {
  target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}


/* ============================================================
   1. PRODUCT GALLERY
   ============================================================ */

class ProductGallery {
  constructor() {
    /** @type {HTMLElement|null} */
    this.mainWrap = document.querySelector('[data-main-image-wrap]');
    /** @type {HTMLImageElement|null} */
    this.mainImg = document.getElementById('mb-main-img');
    /** @type {HTMLElement[]} */
    this.thumbs = Array.from(document.querySelectorAll('[data-thumb]'));
    this.currentIndex = 0;
    this._touchStartX = 0;
    this._touchStartY = 0;

    if (!this.mainImg) return;
    this._initThumbnails();
    this._initZoom();
    this._initSwipe();
  }

  /**
   * Swap main image with a fade.
   * @param {string} src
   * @param {string} [alt]
   */
  setMainImage(src, alt) {
    if (!this.mainImg) return;
    this.mainImg.style.opacity = '0';
    setTimeout(() => {
      this.mainImg.src = src;
      if (alt) this.mainImg.alt = alt;
      this.mainImg.style.opacity = '1';
      this.mainImg.style.transition = 'opacity 0.25s ease';
    }, 180);
  }

  /** @private */
  _initThumbnails() {
    this.thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => this._activateThumb(idx, thumb));

      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const next = this.thumbs[idx + 1];
          if (next) { next.focus(); this._activateThumb(idx + 1, next); }
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prev = this.thumbs[idx - 1];
          if (prev) { prev.focus(); this._activateThumb(idx - 1, prev); }
        }
      });
    });
  }

  /**
   * @param {number} idx
   * @param {HTMLElement} thumbEl
   * @private
   */
  _activateThumb(idx, thumbEl) {
    this.currentIndex = idx;
    const src = thumbEl.dataset.src;
    const alt = thumbEl.dataset.alt;
    if (src) this.setMainImage(src, alt);

    this.thumbs.forEach((t) => {
      t.classList.remove('mb-thumb--active');
      t.setAttribute('aria-pressed', 'false');
    });
    thumbEl.classList.add('mb-thumb--active');
    thumbEl.setAttribute('aria-pressed', 'true');
    thumbEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  /**
   * Activate a thumbnail by index (called from VariantSelector).
   * @param {number} idx
   */
  activateThumbByIndex(idx) {
    const thumb = this.thumbs[idx];
    if (thumb) this._activateThumb(idx, thumb);
  }

  /**
   * Activate the thumbnail whose src matches an image src fragment.
   * @param {string} imageSrc
   * @param {string} [alt]
   */
  activateThumbBySrc(imageSrc, alt) {
    const basename = imageSrc.split('?')[0].split('/').pop().split('.')[0];
    const match = this.thumbs.findIndex((t) => {
      const tSrc = t.dataset.src || '';
      return tSrc.includes(basename);
    });
    if (match >= 0) {
      this._activateThumb(match, this.thumbs[match]);
    } else {
      this.setMainImage(imageSrc, alt);
    }
  }

  /** @private */
  _initZoom() {
    if (!this.mainWrap || !this.mainImg) return;
    const wrap = this.mainWrap;
    const img = this.mainImg;

    wrap.addEventListener('mouseenter', () => {
      img.style.transition = 'transform 0.3s ease';
    });
    wrap.addEventListener('mousemove', (e) => {
      const rect = wrap.getBoundingClientRect();
      const xPct = ((e.clientX - rect.left) / rect.width) * 100;
      const yPct = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${xPct}% ${yPct}%`;
      img.style.transform = 'scale(1.65)';
      img.style.transition = 'transform 0.1s ease';
    });
    wrap.addEventListener('mouseleave', () => {
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
      img.style.transition = 'transform 0.35s ease';
    });
  }

  /** @private */
  _initSwipe() {
    if (!this.mainImg) return;
    this.mainImg.addEventListener('touchstart', (e) => {
      this._touchStartX = e.changedTouches[0].clientX;
      this._touchStartY = e.changedTouches[0].clientY;
    }, { passive: true });

    this.mainImg.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - this._touchStartX;
      const dy = Math.abs(e.changedTouches[0].clientY - this._touchStartY);
      if (Math.abs(dx) < 40 || dy > 60) return;

      if (dx < 0 && this.currentIndex < this.thumbs.length - 1) {
        this._activateThumb(this.currentIndex + 1, this.thumbs[this.currentIndex + 1]);
      } else if (dx > 0 && this.currentIndex > 0) {
        this._activateThumb(this.currentIndex - 1, this.thumbs[this.currentIndex - 1]);
      }
    }, { passive: true });
  }
}


/* ============================================================
   2. VARIANT SELECTOR
   ============================================================ */

class VariantSelector {
  /**
   * @param {HTMLFormElement} form
   * @param {ShopifyVariant[]} variants
   * @param {ProductGallery} gallery
   */
  constructor(form, variants, gallery) {
    this.form = form;
    this.variants = variants;
    this.gallery = gallery;
    this.currentVariant = variants.find(v => v.available) || variants[0];
    this._bindEvents();
  }

  /**
   * Read checked radio inputs to get selected options array.
   * @returns {string[]}
   */
  _getSelectedOptions() {
    const options = [];
    const checked = this.form.querySelectorAll('input[type="radio"]:checked');
    checked.forEach((input) => {
      const idx = parseInt(input.dataset.optionIndex, 10);
      if (!isNaN(idx)) options[idx] = input.value;
    });
    return options;
  }

  /**
   * Find a variant matching option values.
   * @param {string[]} options
   * @returns {ShopifyVariant|undefined}
   */
  _findVariant(options) {
    return this.variants.find((v) =>
      options.every((val, i) => v.options[i] === val)
    );
  }

  /**
   * Update all DOM for newly selected variant.
   * @param {ShopifyVariant} variant
   */
  _updateForVariant(variant) {
    if (!variant) return;
    this.currentVariant = variant;
    this._updatePrice(variant);
    this._updateAvailability(variant);
    this._updateImage(variant);
    this._updateVariantInput(variant);
    this._updateURL(variant);
  }

  /** @param {ShopifyVariant} v */
  _updatePrice(v) {
    const priceEl = this.form.closest('[data-section-type="main-product"]')
      ?.querySelector('[data-price]');
    const compareEl = this.form.closest('[data-section-type="main-product"]')
      ?.querySelector('[data-compare-price]');
    const saveEl = this.form.closest('[data-section-type="main-product"]')
      ?.querySelector('.mb-price-save');
    const stickyPrice = document.querySelector('[data-sticky-price]');

    if (priceEl) priceEl.textContent = formatMoney(v.price);
    if (stickyPrice) stickyPrice.textContent = formatMoney(v.price);

    if (compareEl) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        compareEl.textContent = formatMoney(v.compare_at_price);
        compareEl.style.display = '';
        if (saveEl) {
          const pct = Math.round((v.compare_at_price - v.price) / v.compare_at_price * 100);
          saveEl.textContent = `Save ${pct}%`;
          saveEl.style.display = '';
        }
      } else {
        compareEl.style.display = 'none';
        if (saveEl) saveEl.style.display = 'none';
      }
    }
  }

  /** @param {ShopifyVariant} v */
  _updateAvailability(v) {
    const atcBtn = document.querySelector('[data-atc-btn]');
    const atcText = document.querySelector('[data-atc-text]');
    const stickyAtcBtn = document.querySelector('[data-sticky-atc]');

    const available = v.available;

    if (atcBtn) {
      atcBtn.disabled = !available;
      atcBtn.setAttribute('aria-disabled', String(!available));
    }
    if (atcText) atcText.textContent = available ? 'Add to Cart' : 'Sold Out';
    if (stickyAtcBtn) stickyAtcBtn.disabled = !available;

    // Sale badge visibility
    const saleBadge = document.querySelector('.mb-badge--sale');
    if (saleBadge) {
      saleBadge.style.display =
        v.compare_at_price && v.compare_at_price > v.price ? '' : 'none';
    }
    const soldBadge = document.querySelector('.mb-badge--sold');
    if (soldBadge) soldBadge.style.display = available ? 'none' : '';
  }

  /** @param {ShopifyVariant} v */
  _updateImage(v) {
    if (!v.featured_image || !this.gallery) return;
    this.gallery.activateThumbBySrc(v.featured_image.src, v.featured_image.alt || '');
  }

  /** @param {ShopifyVariant} v */
  _updateVariantInput(v) {
    const input = this.form.querySelector('[data-variant-id]');
    if (input) input.value = v.id;
  }

  /** @param {ShopifyVariant} v */
  _updateURL(v) {
    if (!window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    window.history.replaceState({ variantId: v.id }, '', url.toString());
  }

  /**
   * Update the "selected value" label next to option name.
   * @param {number} optionIndex
   * @param {string} value
   */
  _updateSelectedLabel(optionIndex, value) {
    // Find the option group that contains an input with data-option-index=N
    const input = this.form.querySelector(`input[data-option-index="${optionIndex}"]`);
    if (!input) return;
    const group = input.closest('[data-option-group]');
    if (!group) return;
    const label = group.querySelector('[data-option-selected-val]');
    if (label) label.textContent = value;
  }

  _bindEvents() {
    // Radio change handles both swatches and pills
    this.form.addEventListener('change', (e) => {
      const input = e.target;
      if (input.type !== 'radio') return;
      if (!input.hasAttribute('data-option-index')) return;

      const idx = parseInt(input.dataset.optionIndex, 10);
      this._updateSelectedLabel(idx, input.value);

      // Update active classes
      const group = input.closest('[data-option-group]');
      if (group) {
        group.querySelectorAll('[data-swatch-input]').forEach((s) => {
          s.closest('.mb-swatch')?.classList.toggle('mb-swatch--active', s === input);
        });
        group.querySelectorAll('[data-pill-input]').forEach((p) => {
          p.closest('.mb-pill')?.classList.toggle('mb-pill--active', p === input);
        });
      }

      const opts = this._getSelectedOptions();
      const variant = this._findVariant(opts);
      if (variant) this._updateForVariant(variant);
    });
  }
}


/* ============================================================
   3. CART MANAGER
   ============================================================ */

class CartManager {
  constructor() {
    this._loading = false;
  }

  /**
   * Add item to cart.
   * @param {number} variantId
   * @param {number} quantity
   * @param {HTMLButtonElement} [btn]
   * @returns {Promise<ShopifyCart|null>}
   */
  async addItem(variantId, quantity, btn) {
    if (this._loading) return null;
    this._loading = true;
    if (btn) this._setLoadingState(btn, true);

    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.description || 'Could not add to cart.');
      }

      const cart = await this.getCart();
      this._showSuccessState(btn);
      dispatch('MB:cart:open', { cart });
      dispatch('cart:refresh', { cart });
      return cart;

    } catch (err) {
      console.error('[CartManager] addItem:', err);
      if (btn) this._setLoadingState(btn, false);
      this._showError(err.message);
      return null;

    } finally {
      this._loading = false;
    }
  }

  /**
   * Update a cart line quantity.
   * @param {string} lineKey
   * @param {number} quantity
   * @returns {Promise<ShopifyCart|null>}
   */
  updateItem = debounce(async (lineKey, quantity) => {
    try {
      const resp = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });
      if (!resp.ok) throw new Error('Cart update failed.');
      const cart = await resp.json();
      dispatch('cart:refresh', { cart });
      return cart;
    } catch (err) {
      console.error('[CartManager] updateItem:', err);
      return null;
    }
  }, 400);

  /**
   * @returns {Promise<ShopifyCart>}
   */
  async getCart() {
    const resp = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return resp.json();
  }

  /**
   * Animate button to success state for 2.2s.
   * @param {HTMLButtonElement} [btn]
   * @private
   */
  _showSuccessState(btn) {
    if (!btn) return;
    this._setLoadingState(btn, false);
    btn.classList.add('is-success');
    btn.setAttribute('aria-label', 'Item added to cart');

    if (typeof gsap !== 'undefined') {
      gsap.fromTo(btn, { scale: 0.96 }, { scale: 1, duration: 0.32, ease: 'back.out(2)' });
    }

    setTimeout(() => {
      btn.classList.remove('is-success');
      btn.setAttribute('aria-label', 'Add to cart');
    }, 2200);
  }

  /**
   * @param {HTMLButtonElement} btn
   * @param {boolean} loading
   * @private
   */
  _setLoadingState(btn, loading) {
    if (!btn) return;
    btn.classList.toggle('is-loading', loading);
    btn.disabled = loading;

    const spinner = btn.querySelector('.mb-atc-spinner');
    const text = btn.querySelector('[data-atc-text]');
    if (spinner) spinner.hidden = !loading;
    if (text) text.style.opacity = loading ? '0' : '1';
  }

  /**
   * @param {string} message
   * @private
   */
  _showError(message) {
    const errEl = document.querySelector('[data-atc-error]');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.hidden = false;
    setTimeout(() => { errEl.hidden = true; errEl.textContent = ''; }, 5000);
  }
}


/* ============================================================
   4. STICKY BAR
   ============================================================ */

class StickyBar {
  /**
   * @param {HTMLElement} mainCtaEl — the main ATC button (trigger)
   * @param {HTMLElement} stickyBarEl — the sticky bar element
   */
  constructor(mainCtaEl, stickyBarEl) {
    this.mainCta = mainCtaEl;
    this.bar = stickyBarEl;
    this._visible = false;
    if (!this.mainCta || !this.bar) return;
    this._init();
  }

  _init() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this._hide();
          else this._show();
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(this.mainCta);
  }

  _show() {
    if (this._visible) return;
    this._visible = true;
    this.bar.classList.add('is-visible');
    this.bar.setAttribute('aria-hidden', 'false');
  }

  _hide() {
    if (!this._visible) return;
    this._visible = false;
    this.bar.classList.remove('is-visible');
    this.bar.setAttribute('aria-hidden', 'true');
  }
}


/* ============================================================
   5. TABS
   ============================================================ */

class ProductTabs {
  /**
   * @param {HTMLElement} container — .mb-tabs-section
   */
  constructor(container) {
    this.container = container;
    /** @type {HTMLElement[]} */
    this.btns = Array.from(container.querySelectorAll('.mb-tab-btn'));
    this.activeTab = null;
    this._initEvents();
  }

  _initEvents() {
    this.btns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const tabId = btn.dataset.tab;
        if (tabId) this._switchTab(tabId, btn);
      });

      btn.addEventListener('keydown', (e) => {
        const idx = this.btns.indexOf(btn);
        let next = null;
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          next = this.btns[(idx + 1) % this.btns.length];
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          next = this.btns[(idx - 1 + this.btns.length) % this.btns.length];
        }
        if (next) {
          next.focus();
          this._switchTab(next.dataset.tab, next);
        }
      });
    });
  }

  /**
   * @param {string} tabId
   * @param {HTMLElement} clickedBtn
   * @private
   */
  _switchTab(tabId, _btn) {
    if (tabId === this.activeTab) return;
    this.activeTab = tabId;

    // Buttons
    this.btns.forEach((b) => {
      const isActive = b.dataset.tab === tabId;
      b.classList.toggle('mb-tab-btn--active', isActive);
      b.setAttribute('aria-selected', String(isActive));
      b.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Panels — matched by id="mb-tab-{tabId}"
    const panelId = `mb-tab-${tabId}`;
    this.container.querySelectorAll('.mb-tab-panel').forEach((panel) => {
      const isActive = panel.id === panelId;
      panel.hidden = !isActive;
      panel.classList.toggle('mb-tab-panel--active', isActive);

      if (isActive && typeof gsap !== 'undefined') {
        gsap.fromTo(panel,
          { opacity: 0, y: 8 },
          { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' }
        );
      }
    });
  }
}


/* ============================================================
   6. RECENTLY VIEWED
   ============================================================ */

class RecentlyViewed {
  constructor() {
    this.KEY = 'mb_recently_viewed';
    this.MAX = 6;
  }

  /**
   * @param {ViewedProduct} product
   */
  addProduct(product) {
    if (!product?.id) return;
    let items = this._getAll();
    items = items.filter((p) => p.id !== product.id);
    items.unshift({
      id: product.id,
      title: product.title,
      url: product.url,
      image: product.image || '',
      formattedPrice: product.formattedPrice || formatMoney(product.price || 0),
      price: product.price || 0
    });
    items = items.slice(0, this.MAX);
    try { sessionStorage.setItem(this.KEY, JSON.stringify(items)); } catch (_) {}
  }

  /** @returns {ViewedProduct[]} */
  _getAll() {
    try {
      const raw = sessionStorage.getItem(this.KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }

  /**
   * Render cards into a container, excluding current product.
   * @param {HTMLElement} containerEl
   * @param {number} [currentProductId]
   */
  render(containerEl, currentProductId) {
    if (!containerEl) return;
    const products = this._getAll().filter((p) => p.id !== currentProductId);
    const section = containerEl.closest('[data-recently-viewed]');

    if (!products.length) {
      if (section) section.setAttribute('hidden', '');
      return;
    }

    if (section) section.removeAttribute('hidden');
    containerEl.innerHTML = '';

    products.forEach((product) => {
      const card = document.createElement('a');
      card.href = product.url;
      card.className = 'mb-related-card';
      card.setAttribute('aria-label', `${product.title} — ${product.formattedPrice}`);
      card.innerHTML = `
        <div class="mb-related-card-img-wrap">
          ${product.image
            ? `<img src="${product.image}" alt="${product.title}" width="200" height="200" loading="lazy">`
            : ''
          }
        </div>
        <div class="mb-related-card-body">
          <p class="mb-related-card-title">${product.title}</p>
          <p class="mb-related-card-price">${product.formattedPrice}</p>
        </div>
      `;
      containerEl.appendChild(card);
    });

    if (typeof gsap !== 'undefined') {
      gsap.fromTo(
        containerEl.querySelectorAll('.mb-related-card'),
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.07, ease: 'power2.out' }
      );
    }
  }
}


/* ============================================================
   7. RELATED PRODUCTS
   ============================================================ */

class RelatedProducts {
  /**
   * @param {number} productId
   * @param {number} [limit]
   * @returns {Promise<RecommendedProduct[]>}
   */
  async fetchRecommendations(productId, limit = 3) {
    try {
      const resp = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`,
        { headers: { 'Accept': 'application/json' } }
      );
      if (!resp.ok) throw new Error('Recommendations unavailable.');
      const data = await resp.json();
      return data.products || [];
    } catch (err) {
      console.warn('[RelatedProducts]', err);
      return [];
    }
  }

  /**
   * @param {HTMLElement} grid
   * @param {RecommendedProduct[]} products
   */
  render(grid, products) {
    if (!grid) return;
    grid.innerHTML = '';

    if (!products.length) {
      const section = document.querySelector('[data-related-products]');
      if (section) section.style.display = 'none';
      return;
    }

    products.forEach((product) => {
      const price = formatMoney(product.price);
      const card = document.createElement('a');
      card.href = product.url || `/products/${product.handle}`;
      card.className = 'mb-related-card';
      card.setAttribute('aria-label', product.title);

      card.innerHTML = `
        <div class="mb-related-card-img-wrap">
          ${product.featured_image
            ? `<img src="${product.featured_image}" alt="${product.title}" width="400" height="400" loading="lazy">`
            : ''
          }
        </div>
        <div class="mb-related-card-body">
          <h3 class="mb-related-card-title">${product.title}</h3>
          <p class="mb-related-card-price">${price}</p>
        </div>
      `;
      grid.appendChild(card);
    });

    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      gsap.fromTo(
        grid.querySelectorAll('.mb-related-card'),
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.6, stagger: 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: grid, start: 'top 82%', once: true }
        }
      );
    }
  }

  /**
   * @param {number} productId
   */
  async init(productId) {
    const grid = document.querySelector('[data-related-grid]');
    if (!grid || !productId) return;

    const products = await this.fetchRecommendations(productId);
    this.render(grid, products);
  }
}


/* ============================================================
   8. QUANTITY STEPPER
   ============================================================ */

function initQuantityStepper() {
  const minus = document.querySelector('[data-qty-minus]');
  const plus  = document.querySelector('[data-qty-plus]');
  const input = document.querySelector('[data-qty-input]');
  if (!minus || !plus || !input) return;

  minus.addEventListener('click', () => {
    const val = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 1;
    if (val > min) input.value = val - 1;
  });

  plus.addEventListener('click', () => {
    const val = parseInt(input.value, 10) || 1;
    const max = parseInt(input.max, 10) || 999;
    if (val < max) input.value = val + 1;
  });

  input.addEventListener('change', () => {
    const val = parseInt(input.value, 10);
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 999;
    if (isNaN(val) || val < min) input.value = min;
    else if (val > max) input.value = max;
  });
}


/* ============================================================
   BUNDLE SELECTOR
   ============================================================ */

/**
 * Clicking a bundle card sets quantity and marks card active.
 * @param {HTMLElement} sectionEl
 * @param {HTMLFormElement|null} form
 */
function initBundleSelector(sectionEl, form) {
  const bundleCards = Array.from(sectionEl.querySelectorAll('[data-bundle-card]'));
  const qtyInput = form?.querySelector('[data-qty-input]');
  if (!bundleCards.length || !qtyInput) return;

  bundleCards.forEach((card) => {
    const radio = card.querySelector('[data-bundle-radio]');
    if (!radio) return;

    card.addEventListener('click', () => {
      const qty = parseInt(card.dataset.qty, 10) || 1;
      qtyInput.value = qty;

      // Sync radio
      radio.checked = true;

      // Visual active state
      bundleCards.forEach((c) => c.classList.remove('mb-bundle-card--active'));
      card.classList.add('mb-bundle-card--active');
    });
  });
}


/* ============================================================
   SUBSCRIBE & SAVE TOGGLE
   ============================================================ */

/**
 * Subscribe/one-time toggle: updates price display and perks visibility.
 * @param {HTMLElement} sectionEl
 */
function initPurchaseOptions(sectionEl) {
  const poCards = Array.from(sectionEl.querySelectorAll('[data-po-card]'));
  if (!poCards.length) return;

  // Base price stored on article data attribute (in cents)
  const basePriceCents = parseInt(
    sectionEl.dataset.variantPrice || '0', 10
  );

  poCards.forEach((card) => {
    const radio = card.querySelector('[data-po-radio]');
    if (!radio) return;

    card.addEventListener('click', () => {
      const poType = card.dataset.po;

      // Sync radio
      radio.checked = true;

      // Visual active state
      poCards.forEach((c) => c.classList.remove('mb-po-card--active'));
      card.classList.add('mb-po-card--active');

      // Show/hide perks
      poCards.forEach((c) => {
        const perks = c.querySelector('[data-subscribe-perks]');
        if (perks) perks.classList.toggle('is-hidden', c !== card || poType !== 'subscribe');
      });

      // Update price in the subscribe card if base price is available
      if (basePriceCents) {
        const subPriceEl = sectionEl.querySelector('[data-subscribe-price]');
        const onetimePriceEl = sectionEl.querySelector('[data-onetime-price]');
        if (subPriceEl) subPriceEl.textContent = formatMoney(Math.round(basePriceCents * 0.9));
        if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(basePriceCents);
      }
    });
  });
}


/* ============================================================
   MAIN INIT
   ============================================================ */

/** @type {RecentlyViewed} */
const recentlyViewedStore = new RecentlyViewed();

// Expose for inline Liquid scripts
window.MB = window.MB || {};
window.MB.RecentlyViewed = recentlyViewedStore;

function initProductPage() {
  const sectionEl = document.querySelector('[data-section-type="main-product"]');
  if (!sectionEl) return;

  const sectionId = sectionEl.dataset.sectionId;
  const productId = parseInt(sectionEl.dataset.productId, 10);

  // 1. Gallery
  const gallery = new ProductGallery();

  // 2. Variants
  const variantsScript = document.getElementById(`mb-variants-${sectionId}`);
  const form = document.getElementById(`product-form-${sectionId}`);

  if (variantsScript && form) {
    try {
      const variants = JSON.parse(variantsScript.textContent);
      new VariantSelector(form, variants, gallery);
    } catch (e) {
      console.warn('[MB] Variants JSON parse error:', e);
    }
  }

  // 3. Cart
  const cartManager = new CartManager();

  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[data-atc-btn]');
      const variantInput = form.querySelector('[data-variant-id]');
      const qtyInput = form.querySelector('[data-qty-input]');
      if (!variantInput) return;
      await cartManager.addItem(
        parseInt(variantInput.value, 10),
        parseInt(qtyInput?.value || '1', 10),
        btn
      );
    });
  }

  // Sticky bar ATC
  const stickyAtcBtn = document.querySelector('[data-sticky-atc]');
  if (stickyAtcBtn && form) {
    stickyAtcBtn.addEventListener('click', async () => {
      const variantInput = form.querySelector('[data-variant-id]');
      const qtyInput = form.querySelector('[data-qty-input]');
      if (!variantInput) return;
      await cartManager.addItem(
        parseInt(variantInput.value, 10),
        parseInt(qtyInput?.value || '1', 10),
        stickyAtcBtn
      );
    });
  }

  // 4. Quantity stepper
  initQuantityStepper();

  // 4b. Bundle cards → set quantity + visual state
  initBundleSelector(sectionEl, form);

  // 4c. Subscribe & Save toggle → update price display
  initPurchaseOptions(sectionEl);

  // 5. Sticky bar
  const mainAtcBtn = document.querySelector('[data-atc-btn]');
  const stickyBarEl = document.querySelector('[data-sticky-bar]');
  if (mainAtcBtn && stickyBarEl) new StickyBar(mainAtcBtn, stickyBarEl);

  // 6. Tabs
  const tabsSection = document.querySelector('.mb-tabs-section');
  if (tabsSection) new ProductTabs(tabsSection);

  // 7. Related products
  if (productId) new RelatedProducts().init(productId);

  // 8. Recently viewed — render others
  const rvGrid = document.querySelector('[data-rv-grid]');
  if (rvGrid) recentlyViewedStore.render(rvGrid, productId);

  // Flush queued RV additions from inline script
  if (Array.isArray(window.__mbRVQueue)) {
    window.__mbRVQueue.forEach((p) => recentlyViewedStore.addProduct(p));
    window.__mbRVQueue = [];
  }

  // 9. Save current product to recently viewed
  if (productId) {
    const titleEl = document.querySelector('.mb-title');
    const mainImg = document.getElementById('mb-main-img');
    const priceEl = document.querySelector('[data-price]');
    recentlyViewedStore.addProduct({
      id: productId,
      title: titleEl?.textContent?.trim() || '',
      url: window.location.pathname,
      image: mainImg?.src || '',
      formattedPrice: priceEl?.textContent?.trim() || '',
      price: 0
    });
  }
}

/* ── Bootstrap ─────────────────────────────────────────────── */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductPage);
} else {
  initProductPage();
}

// Theme editor re-init
document.addEventListener('shopify:section:load', (e) => {
  if (e.detail?.sectionId && document.querySelector('[data-section-type="main-product"]')) {
    initProductPage();
  }
});
